/*
  AuthMenu — header entry-point. When anonymous, shows a "Sign in" pill.
  When signed-in, shows an initials avatar with a dropdown (account / sign out).
  Hides itself entirely when feature_accounts is off.
*/
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, User as UserIcon, Loader2, UserCog, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { isFeatureEnabled } from "./FeatureGate";
import AuthModal from "./AuthModal";
import { toast } from "sonner";

function initialsOf(name, email) {
  const n = (name || "").trim();
  if (n) {
    const parts = n.split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }
  return (email || "?").charAt(0).toUpperCase();
}

export default function AuthMenu() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [enabled, setEnabled] = useState(isFeatureEnabled("feature_accounts"));
  const ref = useRef(null);

  useEffect(() => {
    const h = () => setEnabled(isFeatureEnabled("feature_accounts"));
    window.addEventListener("vh-config-loaded", h);
    return () => window.removeEventListener("vh-config-loaded", h);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); }
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuOpen]);

  if (!enabled) return null;
  if (loading) {
    return <Loader2 data-testid="auth-menu-loading" className="h-4 w-4 animate-spin" style={{ color: "var(--text-muted)" }} />;
  }

  if (!user) {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          data-testid="auth-signin-button"
          className="inline-flex items-center gap-2 px-3 sm:px-4 h-9 rounded-lg text-sm font-semibold border hover:bg-[color:var(--surface-2)] transition-colors"
          style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
        >
          <UserIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Sign in</span>
        </button>
        <AuthModal open={open} onOpenChange={setOpen} />
      </>
    );
  }

  const initials = initialsOf(user.name, user.email);

  async function onLogout() {
    setMenuOpen(false);
    await logout();
    toast.success("Signed out");
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="auth-avatar-button"
        className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold text-white"
        style={{ background: "var(--primary)" }}
        aria-label="Account menu"
        title={user.email}
      >{initials}</button>
      {menuOpen && (
        <div
          data-testid="auth-avatar-menu"
          className="absolute right-0 top-11 min-w-[220px] rounded-xl border shadow-lg overflow-hidden z-50"
          style={{ background: "var(--surface)", borderColor: "var(--border-subtle)" }}
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="text-sm font-semibold truncate" style={{ color: "var(--text-main)" }}>
              {user.name || user.email}
            </div>
            {user.name && (
              <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{user.email}</div>
            )}
          </div>
          <Link
            to="/account"
            onClick={() => setMenuOpen(false)}
            data-testid="auth-menu-account"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[color:var(--surface-2)]"
            style={{ color: "var(--text-main)" }}
          >
            <UserCog className="h-4 w-4" /> Your account
          </Link>
          <Link
            to="/family"
            onClick={() => setMenuOpen(false)}
            data-testid="auth-menu-family"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[color:var(--surface-2)]"
            style={{ color: "var(--text-main)" }}
          >
            <Users className="h-4 w-4" /> Family
          </Link>
          <button
            onClick={onLogout}
            data-testid="auth-signout"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[color:var(--surface-2)] border-t"
            style={{ color: "var(--text-main)", borderColor: "var(--border-subtle)" }}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
